import {
  WORLD_SOURCE_FOLDER_DESCRIPTION_MAX_CHARS,
  WORLD_SOURCE_FOLDER_NAME_MAX_CHARS,
  type EnvironmentId,
} from "@cleo/contracts";
import { FolderOpenIcon, FolderPlusIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { SettingsRow } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { isElectron } from "~/env";
import { readLocalApi } from "~/localApi";
import { refreshRoomUniversesForWorkspaceBinding } from "~/neucleos/roomUniverseStore";
import {
  readStatus,
  startNeucleosSignIn,
  statusDescription,
  type NeucleosStatusRead,
  type NeucleosStatusWorld,
} from "~/neucleos/loginCeremony";
import { refreshNeucleosWorlds, useNeucleosWorldStore } from "~/neucleos/worldStore";
import { WorldIntegrations } from "./WorldIntegrations";
import { MacForToolsSwitch } from "./MacForToolsSwitch";
import {
  defaultWorldHomeFolder,
  useWorldWorkspaceBindings,
} from "~/neucleos/worldWorkspaceBinding";
import {
  projectWorldWorkspaceBindingSettingsRows,
  type BoundWorldBindingSettingsRow,
  type WorldBindingSettingsProject,
} from "~/neucleos/worldWorkspaceBindingSettings";
import { useProjects } from "~/state/entities";
import { useEnvironments, usePrimaryEnvironmentId } from "~/state/environments";
import { serverEnvironment } from "~/state/server";
import { useAtomCommand } from "~/state/use-atom-command";
import { readWorldFolderChoices } from "~/neucleos/worldFolderChoices";

/**
 * Compatibility exports for legacy Settings and the shell doors (the Desk
 * dock, the re-auth door): the sign-in and sign-out ceremony is
 * presentation-neutral and lives in `~/neucleos/loginCeremony` so Settings
 * V1 and V2 cannot drift into different sign-in behavior.
 */
export {
  isSettledStatus,
  readStatus,
  readStatusRefreshingWorlds,
  signOutOfNeucleos,
  startNeucleosSignIn,
  statusDescription,
  watchLoginSettlement,
  type NeucleosStatusRead,
} from "~/neucleos/loginCeremony";

type NeucleosWorldStatus = NeucleosStatusWorld;

/**
 * When the World's MCP token lapses, in the viewer's own time. A time alone
 * means today; any other day is named (7 Oct 2026: "live until 5:59 PM",
 * read in the evening, looked already expired).
 */
export function tokenExpiryLabel(expiresAt: number, now: number = Date.now()): string {
  const at = new Date(expiresAt);
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const today = new Date(now);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(at, today)) return time;
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (sameDay(at, tomorrow)) return `tomorrow ${time}`;
  const day = at.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    ...(at.getFullYear() !== today.getFullYear() ? { year: "numeric" as const } : {}),
  });
  return `${day}, ${time}`;
}

/** Status area for a CONNECTED session: the entitled Worlds plus the Arc A4
 * exact Canvas read probe. Rendered only when signed in with a live MCP
 * token, so the probe never appears to a signed-out user. */
export function ConnectionStatusDetails({
  worlds,
}: {
  readonly worlds: ReadonlyArray<NeucleosWorldStatus>;
}) {
  return (
    <div className="space-y-3">
      {/* CN1: World-scoped integrations live above the session custody —
          distinct kinds on one page, never merged. */}
      <WorldIntegrations />
      <WorldList worlds={worlds} />
    </div>
  );
}

function WorldList({ worlds }: { readonly worlds: ReadonlyArray<NeucleosWorldStatus> }) {
  return (
    <ul className="space-y-1" aria-label="Entitled Worlds">
      {worlds.map((world) => (
        <li key={world.worldKey} className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium text-foreground">{world.name}</span>
          <span className="font-mono text-[11px] text-muted-foreground/70">{world.worldKey}</span>
          <span>
            {world.mcpTokenPresent
              ? `· MCP token live until ${tokenExpiryLabel(world.expiresAt)}`
              : "· MCP token absent"}
          </span>
        </li>
      ))}
    </ul>
  );
}

type PersistBindingPatch = (input: {
  readonly environmentId: EnvironmentId;
  readonly input: {
    readonly patch: {
      readonly worldWorkspaceBindings?: Record<
        string,
        {
          readonly filesRoot?: string;
          readonly folders?: ReadonlyArray<string>;
          readonly sourceFolders?: {
            readonly codebase: ReadonlyArray<string>;
            readonly files: ReadonlyArray<string>;
            /** WB6: each listed path's person-given name and description. */
            readonly details?: Readonly<
              Record<
                string,
                { readonly name?: string | undefined; readonly description?: string | undefined }
              >
            >;
          };
        }
      >;
      readonly worldWorkspaceBindingRemovals?: ReadonlyArray<string>;
    };
  };
}) => Promise<unknown>;

/** WB6: the label a listed folder wears when the person has not named it. */
export function folderLeafName(path: string): string {
  const leaf = path
    .replace(/[\\/]+$/, "")
    .split(/[\\/]/)
    .at(-1);
  return leaf === undefined || leaf === "" ? path : leaf;
}

type FolderDetails = NonNullable<BoundWorldBindingSettingsRow["sourceFolders"]>["details"];

/** WB6: only a listed path keeps a detail, and a detail with nothing in it
 * is absence — the same law the server ceremony applies on write. */
function retainedFolderDetails(
  details: FolderDetails | undefined,
  listed: ReadonlyArray<string>,
): FolderDetails | undefined {
  if (details === undefined) return undefined;
  const kept: Record<string, { name?: string; description?: string }> = {};
  for (const path of listed) {
    const detail = details[path];
    if (detail === undefined) continue;
    const name = detail.name?.trim();
    const description = detail.description?.trim();
    if (!name && !description) continue;
    kept[path] = { ...(name ? { name } : {}), ...(description ? { description } : {}) };
  }
  return Object.keys(kept).length === 0 ? undefined : kept;
}

/** Explicit folder lists share the same settings door and never edit a session's selection. */
export function WorldFolderBindingControls({
  row,
  persist,
  canBrowseFolders,
  refreshCatalog,
}: {
  readonly row: Omit<BoundWorldBindingSettingsRow, "kind">;
  readonly persist: PersistBindingPatch;
  readonly canBrowseFolders: boolean;
  readonly refreshCatalog: boolean;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<{
    slot: "codebase" | "files";
    index?: number;
    draft: string;
  } | null>(null);
  // WB6: one inline detail editor at a time — the name or the description of
  // one listed folder. Whitespace clears the value.
  const [detailEditor, setDetailEditor] = useState<{
    path: string;
    field: "name" | "description";
    draft: string;
  } | null>(null);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const legacyKey =
    row.sourceFolders === null &&
    (row.folders !== null || row.filesRoot !== null || row.projectId !== null)
      ? JSON.stringify([row.environmentId, row.worldKey, row.folders, row.filesRoot, row.projectId])
      : null;
  const [legacy, setLegacy] = useState<{
    key: string;
    choices?: { codebase: string[]; files: string[] };
    error?: string;
  } | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (legacyKey === null) return;
    const controller = new AbortController();
    void readWorldFolderChoices(
      row.worldKey,
      row.environmentId,
      primaryEnvironmentId,
      controller.signal,
    ).then(
      (choices) => {
        if (!controller.signal.aborted) setLegacy({ key: legacyKey, choices });
      },
      (cause) => {
        if (!controller.signal.aborted)
          setLegacy({
            key: legacyKey,
            error: cause instanceof Error ? cause.message : "The saved folders could not be read.",
          });
      },
    );
    return () => controller.abort();
  }, [legacyKey, row.worldKey, row.environmentId, primaryEnvironmentId, retry]);
  const chosen: NonNullable<BoundWorldBindingSettingsRow["sourceFolders"]> | undefined =
    row.sourceFolders ??
    (legacyKey === null
      ? { codebase: [], files: [] }
      : legacy?.key === legacyKey
        ? legacy.choices
        : undefined);
  if (chosen === undefined) {
    const readError = legacy?.key === legacyKey ? legacy.error : undefined;
    return (
      <div className="space-y-2 text-xs text-muted-foreground">
        <p role={readError ? "alert" : "status"}>{readError ?? "Reading saved folders…"}</p>
        {readError ? (
          <Button
            size="xs"
            variant="outline"
            onClick={() => {
              setLegacy(null);
              setRetry((value) => value + 1);
            }}
          >
            Try again
          </Button>
        ) : null}
      </div>
    );
  }
  const worldName = row.worldName ?? row.worldKey;
  const writeSourceFolders = async (next: {
    readonly codebase: ReadonlyArray<string>;
    readonly files: ReadonlyArray<string>;
    readonly details?: FolderDetails | undefined;
  }) => {
    setPending(true);
    setError(null);
    try {
      const details = retainedFolderDetails(next.details, [...next.codebase, ...next.files]);
      await persist({
        environmentId: row.environmentId,
        input: {
          patch: {
            worldWorkspaceBindings: {
              [row.worldKey]: {
                sourceFolders: {
                  codebase: next.codebase,
                  files: next.files,
                  ...(details === undefined ? {} : { details }),
                },
              },
            },
          },
        },
      });
      setEditor(null);
      setDetailEditor(null);
      await refreshRoomUniversesForWorkspaceBinding({
        worldKey: row.worldKey,
        environmentId: row.environmentId,
        refreshCatalog,
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The folders could not be saved.");
    } finally {
      setPending(false);
    }
  };
  const writeSlot = (slot: "codebase" | "files", paths: ReadonlyArray<string>) =>
    writeSourceFolders({
      ...chosen,
      [slot]: [...new Set(paths.map((path) => path.trim()).filter(Boolean))],
    });
  const savePath = (slot: "codebase" | "files", path: string, index?: number) => {
    const paths = [...chosen[slot]];
    if (index === undefined) paths.push(path);
    else paths[index] = path;
    return writeSlot(slot, paths);
  };
  // WB6: the person's name or description for one listed path, saved through
  // the same settings door as the path itself.
  const saveDetail = (path: string, field: "name" | "description", value: string) => {
    const current = chosen.details?.[path] ?? {};
    const trimmed = value.trim();
    return writeSourceFolders({
      codebase: chosen.codebase,
      files: chosen.files,
      details: {
        ...chosen.details,
        [path]: { ...current, [field]: trimmed || undefined },
      },
    });
  };
  const browse = async (slot: "codebase" | "files", index?: number) => {
    if (!canBrowseFolders) {
      setEditor({
        slot,
        ...(index === undefined ? {} : { index }),
        draft: index === undefined ? "" : (chosen[slot][index] ?? ""),
      });
      return;
    }
    setError(null);
    setPending(true);
    try {
      const picked = await readLocalApi()?.dialogs.pickFolder({
        initialPath: index === undefined ? null : (chosen[slot][index] ?? null),
      });
      if (typeof picked === "string" && picked.trim()) await savePath(slot, picked, index);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The folder picker could not be opened.");
    } finally {
      setPending(false);
    }
  };
  const detailField = (slot: "codebase" | "files", path: string, itemLabel: string) => {
    if (detailEditor === null || detailEditor.path !== path) return null;
    const field = detailEditor.field;
    const label =
      field === "name"
        ? `${itemLabel} name for ${worldName}`
        : `${itemLabel} description for ${worldName}`;
    const save = () => void saveDetail(path, field, detailEditor.draft);
    return (
      <div className="flex min-w-0 items-start gap-1.5" data-world-source-detail-editor={field}>
        {field === "name" ? (
          <Input
            autoFocus
            aria-label={label}
            className="h-7 min-w-0 flex-1 text-xs"
            data-world-source-name-input={slot}
            disabled={pending}
            maxLength={WORLD_SOURCE_FOLDER_NAME_MAX_CHARS}
            onChange={(event) => setDetailEditor({ ...detailEditor, draft: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                save();
              }
            }}
            placeholder={folderLeafName(path)}
            value={detailEditor.draft}
          />
        ) : (
          <Textarea
            autoFocus
            aria-label={label}
            className="min-h-16 min-w-0 flex-1 text-xs"
            data-world-source-description-input={slot}
            disabled={pending}
            maxLength={WORLD_SOURCE_FOLDER_DESCRIPTION_MAX_CHARS}
            onChange={(event) => setDetailEditor({ ...detailEditor, draft: event.target.value })}
            placeholder="What is in this folder, and how should Cleo treat it?"
            value={detailEditor.draft}
          />
        )}
        <Button size="xs" variant="outline" disabled={pending} onClick={save}>
          Save
        </Button>
        <Button size="xs" variant="ghost" disabled={pending} onClick={() => setDetailEditor(null)}>
          Cancel
        </Button>
      </div>
    );
  };
  const slotList = (slot: "codebase" | "files", title: string, itemLabel: string) => (
    <div className="space-y-1.5" data-world-source-slot={`${row.worldKey}:${slot}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{title}</span>
        <Button
          size="xs"
          variant="ghost"
          disabled={pending}
          aria-label={`Add ${itemLabel} for ${worldName}`}
          onClick={() => void browse(slot)}
        >
          <FolderPlusIcon className="size-3.5" /> Add
        </Button>
      </div>
      {chosen[slot].length === 0 ? (
        <p className="text-xs text-muted-foreground">None selected</p>
      ) : null}
      {chosen[slot].map((path, index) => {
        const detail = chosen.details?.[path];
        return (
          <div key={path} className="flex min-w-0 flex-col gap-1" data-world-source-entry={path}>
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="flex min-w-0 flex-1 flex-col">
                {/* WB6: the person's name is the label; the path stays readable beneath. */}
                <span
                  className="truncate text-sm text-foreground"
                  data-world-source-label={slot}
                  title={path}
                >
                  {detail?.name ?? folderLeafName(path)}
                </span>
                <span
                  className="truncate font-mono text-[11px] text-muted-foreground"
                  title={path}
                  data-world-source-saved={slot}
                >
                  {path}
                </span>
              </span>
              <Button
                size="xs"
                variant="ghost"
                disabled={pending}
                aria-label={`Rename ${itemLabel} ${index + 1} for ${worldName}`}
                onClick={() => setDetailEditor({ path, field: "name", draft: detail?.name ?? "" })}
              >
                Rename
              </Button>
              <Button
                size="xs"
                variant="ghost"
                disabled={pending}
                aria-label={`Describe ${itemLabel} ${index + 1} for ${worldName}`}
                onClick={() =>
                  setDetailEditor({ path, field: "description", draft: detail?.description ?? "" })
                }
              >
                Describe
              </Button>
              <Button
                size="xs"
                variant="outline"
                disabled={pending}
                aria-label={`Browse ${itemLabel} ${index + 1} for ${worldName}`}
                onClick={() => void browse(slot, index)}
              >
                <FolderOpenIcon className="size-3.5" /> {canBrowseFolders ? "Browse" : "Edit"}
              </Button>
              <Button
                size="xs"
                variant="ghost"
                disabled={pending}
                aria-label={`Remove ${itemLabel} ${index + 1} for ${worldName}`}
                onClick={() =>
                  void writeSlot(
                    slot,
                    chosen[slot].filter((_, i) => i !== index),
                  )
                }
              >
                <XIcon className="size-3.5" />
              </Button>
            </div>
            {detail?.description ? (
              <p
                className="whitespace-pre-wrap text-xs text-muted-foreground"
                data-world-source-description={slot}
              >
                {detail.description}
              </p>
            ) : null}
            {detailField(slot, path, itemLabel)}
          </div>
        );
      })}
      {editor?.slot === slot ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <Input
            autoFocus
            aria-label={`${itemLabel} path for ${worldName}`}
            className="h-7 min-w-0 flex-1 font-mono text-xs"
            value={editor.draft}
            disabled={pending}
            placeholder="/path/to/folder"
            onChange={(event) => setEditor({ ...editor, draft: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter" && editor.draft.trim()) {
                event.preventDefault();
                void savePath(slot, editor.draft.trim(), editor.index);
              }
            }}
          />
          <Button
            size="xs"
            variant="outline"
            disabled={pending || !editor.draft.trim()}
            onClick={() => void savePath(slot, editor.draft.trim(), editor.index)}
          >
            Save
          </Button>
          <Button size="xs" variant="ghost" disabled={pending} onClick={() => setEditor(null)}>
            Cancel
          </Button>
        </div>
      ) : null}
      {slot === "files" && chosen.files.length === 0 ? (
        <Button
          data-world-files-default={row.worldKey}
          size="xs"
          variant="outline"
          disabled={pending}
          onClick={() =>
            void savePath("files", defaultWorldHomeFolder(row.worldName, row.worldKey))
          }
        >
          Use default
        </Button>
      ) : null}
    </div>
  );
  return (
    <div className="flex min-w-0 flex-col gap-3" data-world-folder-list={row.worldKey}>
      {/* Cleo Can 35: most people keep documents, not code; folders come first. */}
      {slotList("files", "Folders", "folder")}
      {slotList("codebase", "Code folders", "code folder")}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Every World → workspace binding this machine holds, listed on Settings >
 * World under "Folders" for the entered World (WorldSettings.tsx; moved there
 * from the Neucleos connection row on 10 Sep 2026, WB5), with the World's
 * "Let Cleo use this Mac" switch: live memberships first (bound or awaiting their
 * first binding), then orphaned binding rows whose World has no live grant in
 * this sign-in. Bindings are environment-local settings; clearing one is the
 * explicit removal operation. An unbound World is editable here through the
 * same environment-local settings write used for later changes.
 */
export function NeucleosWorldFoldersRow({
  worldKey: onlyWorldKey,
}: {
  /** WB5: the World page shows the entered World's folders alone. */
  readonly worldKey?: string;
} = {}) {
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const bindingsByEnvironment = useWorldWorkspaceBindings();
  const projects = useProjects();
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const persistServerSettings = useAtomCommand(serverEnvironment.updateSettings);
  const settingsProjects = useMemo<ReadonlyArray<WorldBindingSettingsProject>>(
    () =>
      projects.map((project) => ({
        environmentId: project.environmentId,
        id: project.id,
        title: project.title,
        workspaceRoot: project.workspaceRoot,
      })),
    [projects],
  );
  const rows = useMemo(
    () =>
      projectWorldWorkspaceBindingSettingsRows({
        // The grant deposit already defaults a blank World name to its key;
        // mirror that here so the summary's optional name stays displayable.
        worlds: worlds.map((world) => ({
          worldKey: world.worldKey,
          name: world.name?.trim() || world.worldKey,
        })),
        bindingsByEnvironment,
        projects: settingsProjects,
        unboundEnvironmentId: primaryEnvironmentId,
      }),
    [worlds, bindingsByEnvironment, primaryEnvironmentId, settingsProjects],
  ).filter((row) => onlyWorldKey === undefined || row.worldKey === onlyWorldKey);
  if (rows.length === 0) return null;

  const showEnvironment = bindingsByEnvironment.size > 1;
  const environmentLabel = (environmentId: EnvironmentId) =>
    environments.find((environment) => environment.environmentId === environmentId)?.label ??
    environmentId;

  return (
    <SettingsRow
      {...searchableSetting("world-folders")}
      description="Choose the folders on this Mac that belong to this World. Cleo can search and read everything inside them, including every folder within, in every conversation. Places that hold keys, passwords or private messages stay protected. Code folders are projects Cleo can also work in."
    >
      <ul className="space-y-2 pb-2" aria-label="World workspace bindings">
        {rows.map((row) => (
          <li
            className="space-y-2 rounded-md border border-border/60 px-3 py-2.5"
            key={
              row.kind === "bound"
                ? `${row.environmentId} ${row.worldKey}`
                : `unbound ${row.worldKey}`
            }
          >
            <p className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-sm font-medium text-foreground">
                {row.worldName ?? row.worldKey}
              </span>
              {row.worldName !== null ? (
                <span className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[10px] leading-none text-muted-foreground">
                  {row.worldKey}
                </span>
              ) : (
                <span className="text-muted-foreground">· no live membership in this sign-in</span>
              )}
              {row.kind === "bound" && showEnvironment ? (
                <span className="text-muted-foreground">
                  · {environmentLabel(row.environmentId)}
                </span>
              ) : null}
            </p>
            {row.environmentId !== null ? (
              <WorldFolderBindingControls
                canBrowseFolders={isElectron && row.environmentId === primaryEnvironmentId}
                key={`${row.kind} ${row.environmentId} ${row.worldKey} ${row.filesRoot ?? ""}`}
                persist={persistServerSettings}
                refreshCatalog={row.environmentId === primaryEnvironmentId}
                row={{
                  environmentId: row.environmentId,
                  filesRoot: row.filesRoot,
                  folders: row.folders,
                  projectId: row.projectId,
                  projectTitle: row.projectTitle,
                  sourceFolders: row.sourceFolders,
                  worldKey: row.worldKey,
                  worldName: row.worldName,
                }}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                This device is not ready to store a World workspace binding yet.
              </p>
            )}
            {/* Can 50: the person's own switch for this Mac, only on the row of this Mac and of a World it is signed in to. */}
            {row.worldName !== null &&
            (row.environmentId === null || row.environmentId === primaryEnvironmentId) ? (
              <MacForToolsSwitch worldKey={row.worldKey} worldName={row.worldName} />
            ) : null}
          </li>
        ))}
      </ul>
    </SettingsRow>
  );
}

export function NeucleosConnectionRow() {
  const [status, setStatus] = useState<NeucleosStatusRead>(null);
  const [pending, setPending] = useState(false);
  const [settling, setSettling] = useState(false);
  const stopWatchRef = useRef<(() => void) | null>(null);

  const refresh = useCallback(async () => {
    const next = await readStatus();
    setStatus(next);
    await refreshNeucleosWorlds();
    return next;
  }, []);

  useEffect(() => {
    void refresh();
    return () => stopWatchRef.current?.();
  }, [refresh]);

  const signIn = async () => {
    setPending(true);
    try {
      stopWatchRef.current?.();
      const stop = await startNeucleosSignIn({
        read: refresh,
        onDone: () => setSettling(false),
      });
      if (stop !== null) {
        setSettling(true);
        stopWatchRef.current = stop;
      }
    } finally {
      setPending(false);
    }
  };

  const title = searchableSetting("neucleos-world").title;
  const description = settling
    ? "Completing sign-in. Finish signing in in your browser; this row updates the moment you are signed in."
    : statusDescription(status);
  const resolved = status === "remote_refused" ? null : status;
  const connected = Boolean(resolved?.signedIn && resolved.mcpTokenPresent);
  const worlds = resolved?.worlds ?? [];

  return (
    <>
      <SettingsRow
        id="neucleos-world"
        title={title}
        description={description}
        status={
          connected && worlds.length > 0 ? <ConnectionStatusDetails worlds={worlds} /> : undefined
        }
        control={
          // 7 Oct 2026: a "Sign out" beside the Google Calendar, Gmail and feed cards read as signing out of them,
          // and signed him out of Neucleos. Signing out of Neucleos lives in Account (AccountSession.tsx), and asks
          // first; each integration keeps its own Disconnect on its card.
          connected ? undefined : (
            <Button
              size="sm"
              disabled={!resolved?.configured || pending}
              onClick={() => void signIn()}
            >
              Sign in
            </Button>
          )
        }
      />
    </>
  );
}
